import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Anchor, Trophy, Compass, Target, Flag, Calculator,
  HelpCircle, Lightbulb, Calendar, Sparkles,
} from 'lucide-react';

const ICONS = {
  trophy: Trophy, compass: Compass, target: Target, flag: Flag,
  calculator: Calculator, help: HelpCircle, lightbulb: Lightbulb,
  calendar: Calendar, sparkles: Sparkles, anchor: Anchor,
};

const ICON_COLORS = {
  trophy: 'text-yellow-600', compass: 'text-cyan-700', target: 'text-orange-600',
  flag: 'text-purple-600', calculator: 'text-emerald-700', help: 'text-pink-600',
  lightbulb: 'text-amber-600', calendar: 'text-red-500', sparkles: 'text-indigo-600',
  anchor: 'text-primary',
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
};

const CrewProgressBanner = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPulse = async () => {
      try {
        const res = await api.get('/community/pulse?limit=20');
        if (!cancelled) setItems(res.data || []);
      } catch (err) {
        // Silent fail: banner is non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPulse();
    const interval = setInterval(fetchPulse, 45000); // refresh every 45s
    return () => { cancelled = true; clearInterval(interval); };
  }, [api]);

  if (loading || items.length === 0) return null;

  // Double the list to make the marquee loop seamlessly
  const looped = [...items, ...items];

  return (
    <div
      className="mb-6 relative overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-r from-amber-50/70 via-primary/5 to-amber-50/70 shadow-inner"
      data-testid="crew-progress-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Label : en bandeau au-dessus sur mobile, en pastille latérale sur ordinateur */}
      <div className="md:absolute md:left-0 md:top-0 md:bottom-0 md:z-10 flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary text-primary-foreground md:shadow-lg">
        <Anchor className="w-4 h-4 shrink-0" />
        <span className="text-xs md:text-sm font-semibold whitespace-nowrap tracking-wide">
          L&apos;Équipage progresse
        </span>
      </div>

      {/* Fade edges (ordinateur uniquement) */}
      <div className="pointer-events-none hidden md:block absolute left-[190px] top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-amber-50 to-transparent" />
      <div className="pointer-events-none absolute right-0 bottom-0 top-auto md:top-0 h-10 md:h-auto w-8 z-10 bg-gradient-to-l from-amber-50/90 to-transparent" />

      {/* Scrolling ticker */}
      <div className="pl-3 md:pl-[200px] pr-4 py-2.5 md:py-3 overflow-hidden">
        <div
          ref={scrollerRef}
          className="flex items-center gap-8 whitespace-nowrap"
          style={{
            animation: `crew-marquee ${Math.max(30, items.length * 4)}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {looped.map((item, idx) => {
            const Icon = ICONS[item.icon] || Sparkles;
            const color = ICON_COLORS[item.icon] || 'text-primary';
            return (
              <div
                key={`${item.action}-${item.created_at}-${idx}`}
                className="flex items-center gap-2 text-sm text-foreground/85"
                data-testid="crew-progress-item"
              >
                <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                <span className="font-semibold text-primary">{item.user_first_name}</span>
                <span>{item.verb}</span>
                {item.target && (
                  <span className="italic text-foreground/70">
                    « {item.target.length > 40 ? item.target.slice(0, 40) + '…' : item.target} »
                  </span>
                )}
                <span className="text-xs text-muted-foreground ml-1">· {relTime(item.created_at)}</span>
                <span className="text-primary/40 ml-4">•</span>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes crew-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default CrewProgressBanner;
